import { Signal, computed } from '@angular/core';

/**
 * Returns a computed signal that prefers `config()?.[field]` and falls back to
 * the value of `fallback()` when the config field is `null` or `undefined`.
 *
 * Designed for the dual-API pattern on dynamic components: the component is
 * fed either a whole `config` object (typical when instantiated by the
 * `DynamicElementService` / `CardService` from a JSON page model) or a set of
 * individual `[field]` inputs (typical when the component is composed
 * directly in another template). Templates always read the resolved value,
 * so the dual API is invisible to template authors.
 *
 * Two overloads keep the resolved signal as tightly typed as the inputs allow:
 * when the fallback signal is statically non-nullable (e.g. an `input('', ...)`
 * with a default), the result is also non-nullable, so consumers do not need
 * `?? ''` defenses in templates.
 *
 * @example
 *   class CardArticleComponent {
 *       readonly config = input<ICardArticle | undefined>(undefined);
 *       readonly title  = input('');
 *
 *       protected readonly resolvedTitle = withConfigFallback(
 *           this.config, 'title', this.title,
 *       ); // Signal<string>
 *   }
 */
export function withConfigFallback<T, K extends keyof T, V extends NonNullable<T[K]>>(
    config: Signal<Partial<T> | undefined>,
    field: K,
    fallback: Signal<V>,
): Signal<V>;
export function withConfigFallback<T, K extends keyof T>(
    config: Signal<Partial<T> | undefined>,
    field: K,
    fallback: Signal<T[K] | undefined>,
): Signal<T[K] | undefined>;
export function withConfigFallback<T, K extends keyof T>(
    config: Signal<Partial<T> | undefined>,
    field: K,
    fallback: Signal<T[K] | undefined>,
): Signal<T[K] | undefined> {
    return computed(() => config()?.[field] ?? fallback());
}
